import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel 
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast, ToastContainer } from 'react-toastify';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import UserDetailsModal from '../../Components/Common/UserDetailsModal';
import useUserDetails from '../../hooks/useUserDetails';
import Swal from 'react-bootstrap-sweetalert';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

const ReviewRatings = () => {
    document.title = "Review & Ratings";
    
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });
    const { 
        userDetailsModal, 
        setUserDetailsModal, 
        selectedUser, 
        loadingUser, 
        fetchUserDetails 
    } = useUserDetails();
    const [sort, setSort] = useState({
        column: '',
        direction: 'asc'
    });
    const [sweetAlert, setSweetAlert] = useState(null);
    const [activeTab, setActiveTab] = useState('ACTIVE');

    const fetchReviews = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            let url = `${API_BASE_URL}/admin/reviewRating?limit=${pagination.limit}&offset=${offset}&trigger=${activeTab}`;
            
            if (searchText) {
                url += `&searchText=${encodeURIComponent(searchText)}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            
            if (data.data) {
                setReviews(data.data.data);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.data.totalCount
                }));
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
            toast.error(error.message || 'Failed to fetch reviews', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));

        const sortedReviews = [...reviews].sort((a, b) => {
            if (column === 'slNo') {
                const indexA = reviews.indexOf(a);
                const indexB = reviews.indexOf(b);
                return sort.direction === 'asc' ? indexA - indexB : indexB - indexA;
            }
            let aValue = sort.column.includes('.') ? 
                sort.column.split('.').reduce((obj, key) => obj[key], a) : 
                a[sort.column];
            let bValue = sort.column.includes('.') ? 
                sort.column.split('.').reduce((obj, key) => obj[key], b) : 
                b[sort.column];

            if (sort.column === 'ratedTs') {
                return sort.direction === 'asc' ? 
                    new Date(aValue) - new Date(bValue) : 
                    new Date(bValue) - new Date(aValue);
            }

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            return sort.direction === 'asc' ? 
                aValue > bValue ? 1 : -1 : 
                aValue < bValue ? 1 : -1;
        });

        setReviews(sortedReviews);
    };

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchReviews(1);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchText]);

    useEffect(() => {
        if (sort.column) {
            const sortedReviews = [...reviews].sort((a, b) => {
                let aValue = sort.column.includes('.') ? 
                    sort.column.split('.').reduce((obj, key) => obj[key], a) : 
                    a[sort.column];
                let bValue = sort.column.includes('.') ? 
                    sort.column.split('.').reduce((obj, key) => obj[key], b) : 
                    b[sort.column];

                if (sort.column === 'ratedTs') {
                    return sort.direction === 'asc' ? 
                        new Date(aValue) - new Date(bValue) : 
                        new Date(bValue) - new Date(aValue);
                }

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                return sort.direction === 'asc' ? 
                    aValue > bValue ? 1 : -1 : 
                    aValue < bValue ? 1 : -1;
            });
            setReviews(sortedReviews);
        }
    }, [sort]);

    useEffect(() => {
        fetchReviews(1);
    }, [activeTab]);

    const getImageUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        
        const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    };

    const renderStars = (rating) => {
        return [...Array(5)].map((_, index) => (
            <i 
                key={index}
                className={`ri-star-${index < rating ? 'fill' : 'line'} fs-16 ${
                    index < rating ? 'text-warning' : 'text-muted'
                }`}
            ></i>
        ));
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        if (totalPages <= 0) return null;

        const getPageNumbers = () => {
            const delta = 2;
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= pagination.currentPage - delta && i <= pagination.currentPage + delta)) {
                    range.push(i);
                }
            }

            const sortedRange = [...new Set(range)].sort((a, b) => a - b);

            for (let i of sortedRange) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">
                        Showing <span className="fw-semibold">{Math.min((pagination.currentPage - 1) * pagination.limit + 1, pagination.totalCount)}-{Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results
                    </div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchReviews(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                <i className="ri-arrow-left-s-line"></i>
                            </Button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNumber === pagination.currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                            >
                                <Button
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && fetchReviews(pageNumber)}
                                    disabled={pageNumber === '...'}
                                >
                                    {pageNumber}
                                </Button>
                            </li>
                        ))}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchReviews(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            >
                                <i className="ri-arrow-right-s-line"></i>
                            </Button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    const handleSuspendReview = (reviewId) => {
        setSweetAlert(
            <Swal
                warning
                showCancel
                confirmBtnText="Yes, suspend it!"
                confirmBtnBsStyle="danger"
                title="Are you sure?"
                onConfirm={() => {
                    setSweetAlert(null);
                    suspendReview(reviewId);
                }}
                onCancel={() => setSweetAlert(null)}
                focusCancelBtn
            >
                You want to suspend this review?
            </Swal>
        );
    };

    const suspendReview = async (reviewId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/reviewRating`, {
                method: 'PUT',
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    reviewRatingId: reviewId,
                    trigger: "SUSPENDED"
                })
            });

            if (response.ok) {
                setSweetAlert(
                    <Swal
                        success
                        title="Suspended!"
                        onConfirm={() => {
                            setSweetAlert(null);
                            fetchReviews(pagination.currentPage);
                        }}
                    >
                        Review has been suspended.
                    </Swal>
                );
            } else {
                toast.error('Failed to suspend review', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
            }
        } catch (error) {
            toast.error(error.message || 'Failed to suspend review', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
            console.error('Error suspending review:', error);
            setSweetAlert(
                <Swal
                    error
                    title="Error!"
                    onConfirm={() => setSweetAlert(null)}
                >
                    Failed to suspend review.
                </Swal>
            );
        }
    };

    return (
        <React.Fragment>
            {sweetAlert}
        <div className="page-content">
                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                />
            
            <Container fluid>
                    <BreadCrumb title="Review & Ratings" pageTitle="Reviews" />
                <Row>
                    <Col lg={12}>
                        <Card>
                                <CardHeader className="border-bottom-dashed">
                                    <Row className="align-items-center g-3">
                                        <Col xxl={3} lg={6}>
                                            <div className="search-box">
                                                <Input 
                                                    type="text"
                                                    className="form-control search"
                                                    placeholder="Search by name..."
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </Col>
                                        <Col>
                                            <ul className="nav nav-tabs-custom card-header-tabs border-bottom-0">
                                                <li className="nav-item">
                                                    <Link
                                                        className={`nav-link ${activeTab === 'ACTIVE' ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setActiveTab('ACTIVE');
                                                            setSearchText('');
                                                        }}
                                                        to="#"
                                                    >
                                                        <i className="ri-check-double-line me-1 align-bottom"></i>
                                                        Active Reviews
                                                    </Link>
                                                </li>
                                                <li className="nav-item">
                                                    <Link
                                                        className={`nav-link ${activeTab === 'SUSPENDED' ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setActiveTab('SUSPENDED');
                                                            setSearchText('');
                                                        }}
                                                        to="#"
                                                    >
                                                        <i className="ri-close-circle-line me-1 align-bottom"></i>
                                                        Suspended Reviews
                                                    </Link>
                                                </li>
                                            </ul>
                                        </Col>
                                    </Row>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-striped table-nowrap align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                    <th 
                                                        className="sort-column" 
                                                        onClick={() => handleSort('slNo')}
                                                        style={{ width: '80px' }}
                                                    >
                                                        Sl. No
                                                        {sort.column === 'slNo' && (
                                                            <i className={`ri-${sort.direction === 'asc' ? 'arrow-up' : 'arrow-down'}-s-line ms-1`}></i>
                                                        )}
                                                    </th>
                                                    <th 
                                                        className="sort-column" 
                                                        onClick={() => handleSort('ratedTs')}
                                                    >
                                                        Date & Time
                                                        {sort.column === 'ratedTs' && (
                                                            <i className={`ri-${sort.direction === 'asc' ? 'arrow-up' : 'arrow-down'}-s-line ms-1`}></i>
                                                        )}
                                                    </th>
                                                    <th 
                                                        className="sort-column" 
                                                        onClick={() => handleSort('rating')}
                                                    >
                                                        Rating
                                                        {sort.column === 'rating' && (
                                                            <i className={`ri-${sort.direction === 'asc' ? 'arrow-up' : 'arrow-down'}-s-line ms-1`}></i>
                                                        )}
                                                    </th>
                                                    <th 
                                                        className="sort-column" 
                                                        onClick={() => handleSort('fullName')}
                                                    >
                                                        Rated By
                                                        {sort.column === 'fullName' && (
                                                            <i className={`ri-${sort.direction === 'asc' ? 'arrow-up' : 'arrow-down'}-s-line ms-1`}></i>
                                                        )}
                                                    </th>
                                                    <th 
                                                        className="sort-column" 
                                                        onClick={() => handleSort('reviewToFullName')}
                                                    >
                                                        Rated To
                                                        {sort.column === 'reviewToFullName' && (
                                                            <i className={`ri-${sort.direction === 'asc' ? 'arrow-up' : 'arrow-down'}-s-line ms-1`}></i>
                                                        )}
                                                    </th>
                                                    <th 
                                                        className="sort-column" 
                                                        onClick={() => handleSort('suspendedOn')}
                                                    >
                                                        Status
                                                        {sort.column === 'suspendedOn' && (
                                                            <i className={`ri-${sort.direction === 'asc' ? 'arrow-up' : 'arrow-down'}-s-line ms-1`}></i>
                                                        )}
                                                    </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                                {reviews.map((review, index) => (
                                                    <tr key={review._id}>
                                                        <td>
                                                            {((pagination.currentPage - 1) * pagination.limit) + index + 1}
                                                        </td>
                                                        <td>{moment(review.ratedTs).format('DD MMM YYYY, hh:mm A')}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                {renderStars(review.rating)}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <h5 className="fs-13 mb-1">
                                                                    <Link 
                                                                        to="#" 
                                                                        className="text-body"
                                                                        onClick={() => fetchUserDetails(review.reviewByUserId)}
                                                                    >
                                                                        {review.fullName}
                                                                    </Link>
                                                                </h5>
                                                                <p className="text-muted mb-0">@{review.username}</p>
                                                            </div>
                                                    </td>
                                                    <td>
                                                            <div>
                                                                <h5 className="fs-13 mb-1">
                                                                    <Link 
                                                                        to="#" 
                                                                        className="text-body"
                                                                        onClick={() => fetchUserDetails(review.reviewToUserId)}
                                                                    >
                                                                        {review.reviewToFullName}
                                                        </Link>
                                                                </h5>
                                                                <p className="text-muted mb-0">@{review.reviewToUsername}</p>
                                                            </div>
                                                    </td>
                                                    <td>
                                                            <div className="d-flex gap-2">
                                                                {review.suspendedOn ? (
                                                                    <span className="badge bg-danger-subtle text-danger">
                                                                        Suspended
                                                                    </span>
                                                                ) : (
                                                                    <>
                                                                        <span className="badge bg-success-subtle text-success">
                                                                            Active
                                                                        </span>
                                                                        <Button
                                                                            color="soft-danger"
                                                                            size="sm"
                                                                            className="btn-icon"
                                                                            onClick={() => handleSuspendReview(review._id)}
                                                                            title="Suspend Review"
                                                                        >
                                                                            <i className="ri-delete-bin-line"></i>
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                    </td>
                                                </tr>
                                            ))}
                                                {reviews.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-4 text-muted">
                                                            <i className="ri-information-line fs-4 me-1 align-middle"></i>
                                                            No reviews found
                                                        </td>
                                                    </tr>
                                                )}
                                        </tbody>
                                    </Table>
                                </div>
                                    {reviews.length > 0 && renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            </div>
            {loading && (
                <div className="overlay">
                    <div className="spinner-container">
                        <Spinner color="primary" />
                    </div>
                </div>
            )}
            <UserDetailsModal 
                isOpen={userDetailsModal}
                toggle={() => setUserDetailsModal(!userDetailsModal)}
                user={selectedUser}
                loading={loadingUser}
                getImageUrl={getImageUrl}
            />
            <style>
                {`
                    .cursor-pointer {
                        cursor: pointer;
                    }
                    .cursor-pointer:hover {
                        color: #405189 !important;
                        text-decoration: underline;
                    }
                    .badge {
                        display: inline-flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                        min-height: 22px !important;
                    }
                    .table td {
                        vertical-align: middle !important;
                    }
                `}
            </style>
        </React.Fragment>
    );
};

export default ReviewRatings;